/**
 * **文字与描边** —— UI 里需要 `TextStyle` 的那一半。
 *
 * 墨与量纲住在 [`ink.ts`](./ink.ts)：那边**刻意不 import Pixi**，
 * 所以规范里那几条最要紧的规矩（hover 只加墨、禁用不用透明度）
 * 能在 node 里当成数据验（`test/ui/design.test.ts`）。
 *
 * 这里只负责"怎么把墨刷到字上、怎么把设计尺寸换算成屏幕尺寸"。
 *
 * 完整规范见 [`docs/ui-design.md`](../../../docs/ui-design.md)。
 */

import { TextStyle } from 'pixi.js';
import { FONT_CJK, FONT_DISPLAY, FONT_MONO } from '../theme';
import { roundFont, scaled, scaledFont } from './metrics';
import { INK } from './ink';

// 老的调用点从 `style` 取墨，一个字都不用改
export * from './ink';

export type FontRole = 'mono' | 'display' | 'cjk';

const STACKS: Record<FontRole, string> = {
  mono: FONT_MONO,
  display: FONT_DISPLAY,
  cjk: FONT_CJK,
};

export interface TextStyleSpec {
  readonly scale: number;
  /** 设计尺寸下的字号（未乘倍率） */
  readonly size: number;
  readonly fill?: number;
  readonly role?: FontRole;
  readonly bold?: boolean;
  /** 拉字距。默认 `none` —— 只有机读文本才拉 */
  readonly tracking?: number;
  readonly align?: 'left' | 'center' | 'right';
  /** 给了就自动换行 */
  readonly wrapWidth?: number;
}

/**
 * 造一个 `TextStyle`。
 *
 * ⚠️ 字号走 `scaledFont`（次线性）而不是 `scaled`：线性到 2 倍会让正文
 * 变成 28px、一行放不下几个字。理由写在 `metrics.ts`。
 *
 * ⚠️ `breakWords: true` 是必须的：中文没有空格，不开的话长牌名会直接冲出面板。
 */
export function uiTextStyle(spec: TextStyleSpec): TextStyle {
  const size = roundFont(scaledFont(spec.size, spec.scale));
  return new TextStyle({
    fontFamily: STACKS[spec.role ?? 'mono'],
    fontSize: size,
    fill: spec.fill ?? INK.text,
    fontWeight: spec.bold === true ? 'bold' : 'normal',
    letterSpacing: spec.tracking ?? 0,
    align: spec.align ?? 'left',
    wordWrap: spec.wrapWidth !== undefined,
    wordWrapWidth: spec.wrapWidth ?? 0,
    breakWords: true,
  });
}

/** 描边粗细换算。`hair` 在任何倍率下都不小于 1px，否则细线会消失 */
export function uiLine(base: number, scale: number): number {
  return Math.max(1, Math.round(scaled(base, scale)));
}

/** 面板内边距 / 间距换算。给的是"设计尺寸"，随倍率走 */
export function uiPad(base: number, scale: number): number {
  return Math.round(scaled(base, scale));
}
